import { Stack } from '@mui/material';
import {
    DataGridPro,
    DataGridProProps,
    GridRowHeightParams,
    GridColDef,
    GridColumnOrderChangeParams,
    GridColumnResizeParams,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { generateUUIDv4 } from '@/modules/MuiDialog/utils/uuid';
import { uniqBy, includes, merge } from 'lodash';
import BasicGridToolBar, { Props as BasicGridToolBarProps } from './BasicGridToolBar';
import TagFields, { Props as TagProps } from './TagFields';
import ReferenceField, { ReferenceFieldProps } from './ReferenceField';
import CustomHeaderCell from './CustomHeaderCell';
import { styled } from '@mui/material';
import useGridPersistence from './useGridPersistence';

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    letterSpacing: 'normal',

    '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
        borderRight: `1px solid ${theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'}`,
    },

    '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
        borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'}`,

        '&--editable': {
            // backgroundColor: 'red',

            '&:focus, &:focus-within': {
                outline: '1px solid var(--v-palette-text-primary)',

                backgroundColor: 'var(--v-palette-action-hover)',
            },
        },
    },
}));

interface BasicDataGridProProps extends DataGridProProps {
    sortableFields?: string[];
    isAutoHeightRows?: boolean;
    persistenceKey?: string;
}
export interface BasicToolBarProps extends BasicGridToolBarProps {}
export interface TagFieldsProps extends TagProps {}

const autoHeightRowProps = {
    getRowHeight: ({ id, densityFactor }: GridRowHeightParams) => 'auto',
    getEstimatedRowHeight: () => 200,
    sx: {
        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '4px' },
        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '8px' },
        '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '12px' },
    },
};
const NoRowsOverlay = () => {
    return (
        <Stack height="100%" alignItems="center" justifyContent="center">
            일치하는 데이터가 없습니다.
        </Stack>
    );
};

const NoResultOverlay = () => {
    return (
        <Stack height="100%" alignItems="center" justifyContent="center">
            검색 결과가 존재하지 않습니다.
        </Stack>
    );
};
const defaultProps: BasicToolBarProps = {
    components: {
        // Toolbar: BasicGridToolBar,
        NoRowsOverlay: NoRowsOverlay,
        NoResultOverlay: NoResultOverlay,
        BooleanCellFalseIcon: () => <div></div>,
    },
};
const BasicDataGridPro = ({
    columns = [],
    rows = [],
    density = 'standard',
    hideFooter,
    disableColumnMenu = true,
    sortableFields = [],
    isAutoHeightRows = false,
    pageSizeOptions = [],
    persistenceKey,
    ...props
}: BasicDataGridProProps) => {
    // 그리드 상태 유지 훅
    const { savedState, handleColumnOrderChange, handleColumnWidthChange, handlePageSizeChange, isPersistenceEnabled } =
        useGridPersistence({
            persistenceKey,
            defaultPageSize: props.paginationModel?.pageSize || 25,
        });

    // 현재 열 순서를 추적하기 위한 ref
    const columnOrderRef = useRef<string[]>([]);
    const autoGeneratedColumns = useMemo(() => {
        if (!!columns?.length || !rows?.length) return columns;
        const nColumns: GridColDef[] = [];
        const sampleItem = { ...rows[0] };
        Object.entries(sampleItem).forEach(([key, value]) => {
            const typeOfValue = typeof value;
            const type = ['string', 'number', 'boolean'].includes(typeOfValue) ? typeOfValue : 'string';
            nColumns.push({
                field: key,
                headerName: key,
                type: type,
                valueFormatter: (params) => {
                    const { value } = params;

                    if (typeof value === 'object') {
                        return JSON.stringify(value);
                    } else {
                        return value;
                    }
                },
                sortable: includes(sortableFields, key) || false,
                flex: 1,
            });
        });
        return nColumns;
    }, [rows, columns]);
    const computedColumns = useMemo(() => {
        const nColumns: GridColDef[] = [];
        const mainColumns: GridColDef[] = columns.filter((o) => !!o?.flex && o?.flex >= 1);

        columns.forEach((item, index) => {
            const nItem = { ...item };

            if (!nItem?.sortable) nItem.sortable = includes(sortableFields, item?.field) || false;
            if (!mainColumns?.length) nItem.flex = 1;

            // 저장된 열 너비 적용
            if (isPersistenceEnabled && savedState?.columnWidths?.[item.field]) {
                nItem.width = savedState.columnWidths[item.field];
                // 너비가 저장되어 있으면 flex를 제거하여 고정 너비로 표시
                delete nItem.flex;
            }

            nColumns.push(nItem);
        });

        // 저장된 열 순서가 있으면 적용
        if (isPersistenceEnabled && savedState?.columnOrder?.length) {
            const orderedColumns: GridColDef[] = [];
            const columnMap = new Map(nColumns.map((col) => [col.field, col]));

            // 저장된 순서대로 열 배치
            savedState.columnOrder.forEach((field) => {
                const col = columnMap.get(field);
                if (col) {
                    orderedColumns.push(col);
                    columnMap.delete(field);
                }
            });

            // 저장된 순서에 없는 새로운 열은 뒤에 추가
            columnMap.forEach((col) => {
                orderedColumns.push(col);
            });

            // 열 순서 ref 업데이트
            columnOrderRef.current = orderedColumns.map((col) => col.field);

            return orderedColumns;
        }

        // 열 순서 ref 업데이트 (저장된 순서가 없는 경우)
        columnOrderRef.current = nColumns.map((col) => col.field);

        return nColumns || [];
    }, [columns, sortableFields, isPersistenceEnabled, savedState]);
    const computedRows = useMemo(() => {
        const idItems = rows.filter((o) => !!o?.id);
        const identifiedItems = uniqBy(idItems, 'id');

        // 모든 데이터가 unique 한 id를 가진 경우에는 그대로 반환한다.
        if (identifiedItems.length === rows.length) return rows;

        const nRows: any[] = [];

        rows.forEach((item, index) => {
            const nItem: Record<string, any> = { ...item };

            if (!nItem?.id) {
                nItem.instanceId = nItem?.instanceId || generateUUIDv4();
                nItem.index = index;
            }

            nRows.push(nItem);
        });

        return nRows;
    }, [rows]);
    const computedProps = useMemo(() => {
        try {
            const oProps = { ...props };

            let initProps = { ...defaultProps };

            if (!!isAutoHeightRows) {
                initProps = merge({}, defaultProps, autoHeightRowProps);
            }

            return merge({}, initProps, oProps);
        } catch (e) {
            console.error(e);
        }
    }, [props, isAutoHeightRows]);

    // 열 순서 변경 핸들러
    const onColumnOrderChangeHandler = useCallback(
        (params: GridColumnOrderChangeParams) => {
            // 현재 열 순서 업데이트
            const currentOrder = [...columnOrderRef.current];
            currentOrder.splice(params.oldIndex, 1);
            currentOrder.splice(params.targetIndex, 0, params.column.field);
            columnOrderRef.current = currentOrder;

            // 새로운 순서 배열을 직접 전달
            handleColumnOrderChange([...currentOrder]);

            // 기존 핸들러가 있으면 호출
            if (props.onColumnOrderChange) {
                (props.onColumnOrderChange as (params: GridColumnOrderChangeParams) => void)(params);
            }
        },
        [handleColumnOrderChange, props.onColumnOrderChange],
    );

    // 열 너비 변경 핸들러
    const onColumnWidthChangeHandler = useCallback(
        (params: GridColumnResizeParams) => {
            handleColumnWidthChange(params);

            // 기존 핸들러가 있으면 호출
            if (props.onColumnWidthChange) {
                (props.onColumnWidthChange as (params: GridColumnResizeParams) => void)(params);
            }
        },
        [handleColumnWidthChange, props.onColumnWidthChange],
    );

    // 페이지네이션 모델 (저장된 pageSize 적용)
    const computedPaginationModel = useMemo(() => {
        if (isPersistenceEnabled && savedState?.pageSize && props.paginationModel) {
            return {
                page: props.paginationModel.page ?? 0,
                pageSize: savedState.pageSize,
            };
        }
        return props.paginationModel;
    }, [props.paginationModel, isPersistenceEnabled, savedState?.pageSize]);

    // 페이지네이션 변경 핸들러
    const onPaginationModelChangeHandler = useCallback(
        (model: any, details: any) => {
            // pageSize 변경 시 저장
            if (model.pageSize !== computedPaginationModel?.pageSize) {
                handlePageSizeChange(model.pageSize);
            }

            // 기존 핸들러가 있으면 호출
            if (props.onPaginationModelChange) {
                props.onPaginationModelChange(model, details);
            }
        },
        [handlePageSizeChange, props.onPaginationModelChange, computedPaginationModel?.pageSize],
    );
    return (
        <>
            <StyledDataGrid
                rows={computedRows}
                columns={computedColumns?.length ? computedColumns : autoGeneratedColumns}
                getRowId={!!props?.getRowId ? props?.getRowId : (r) => r.id || r.instanceId}
                slots={
                    !!computedProps?.components
                        ? {
                              toolbar: BasicToolBar,
                              ...computedProps?.components,
                          }
                        : { toolbar: BasicToolBar }
                }
                density={density}
                localeText={{
                    toolbarExport: '다운로드',
                    toolbarExportCSV: 'CSV 파일로 다운로드',
                    toolbarColumns: '열 선택',
                    columnsPanelShowAllButton: '전체보기',
                    columnsPanelHideAllButton: '전체 숨기기',
                    columnsPanelTextFieldLabel: '열 검색',
                    columnsPanelTextFieldPlaceholder: '제목',
                    columnsPanelDragIconLabel: '열 재정렬',
                    pinToLeft: '왼쪽에 고정',
                    pinToRight: '오른쪽에 고정',
                    columnMenuManageColumns: '열 조정',
                    unpin: '고정 해제',
                    MuiTablePagination: {
                        labelRowsPerPage: '페이지당 행 수:',
                        labelDisplayedRows: ({ from, to, count }) =>
                            `${from}~${to} / 총 ${count !== -1 ? count : `${to}개 이상`}건`,
                    },
                }}
                isRowSelectable={() => false}
                pageSizeOptions={pageSizeOptions.length > 0 ? pageSizeOptions : [5, 10, 15, 25, 50, 100]}
                // TODO: 1조까지는 가능 여부 확인
                disableColumnMenu={disableColumnMenu}
                getRowClassName={
                    !!props?.getRowClassName
                        ? props.getRowClassName
                        : (params) => {
                              return !!props?.onRowClick ? 'cursor-pointer' : '';
                          }
                }
                hideFooter={hideFooter}
                sx={{
                    '& .MuiDataGrid-row': {
                        cursor: !!props?.onRowClick || !!props?.onRowDoubleClick ? 'pointer' : 'default',
                    },
                }}
                slotProps={computedProps?.slotProps}
                {...computedProps}
                // persistence 관련 props (computedProps 뒤에 위치하여 덮어쓰기 방지)
                onColumnOrderChange={isPersistenceEnabled ? onColumnOrderChangeHandler : props.onColumnOrderChange}
                onColumnWidthChange={isPersistenceEnabled ? onColumnWidthChangeHandler : props.onColumnWidthChange}
                paginationModel={computedPaginationModel}
                onPaginationModelChange={
                    isPersistenceEnabled ? onPaginationModelChangeHandler : props.onPaginationModelChange
                }
            />
            {!!hideFooter && rows.length > 100 ? <div>최대 100개의 데이터만 표시됩니다.</div> : ''}
        </>
    );
};
export const BasicToolBar = BasicGridToolBar;
export { TagFields, CustomHeaderCell, ReferenceField, type ReferenceFieldProps };

export default BasicDataGridPro;
